import * as React from 'react';
import ReactTooltip from 'react-tooltip';
import { Dropdown } from 'react-bootstrap';
import styled from 'styled-components';
import FilterTag from '../TableFilter/FilterTag';
import { LabeledSelect } from '../LabeledSelect';
import EditIcon from '../icons/project-icons/Edit';
import {
  ConfPanelDetailsText,
  ConfPanelInEditModalButtons,
  ConfPanelInEditSaveButton,
  ConfPanelModalButton,
  ConfPanelAddRoomButtonContainer,
  ConfPanelAddRoomButton,
  ConfPanelNameInput,
  ConfPanelFilterTags
} from './SceneInEditDropdownComponents';
import { ButtonColorType } from '../Button';

const Spacer = styled.div`
  height: 20px;
`;

const Text = styled.span`
  font-size: 20px;
`;

const CustomToggle = React.forwardRef<HTMLButtonElement>(
  (buttonProps, buttonRef) => {
    // @ts-ignore
    const { onClick, sceneInEdit, rooms } = buttonProps;
    return (
      <ConfPanelModalButton
        type="button"
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
        ref={buttonRef}
        onMouseEnter={() => ReactTooltip.rebuild()}
      >
        <ConfPanelDetailsText>
          <Text data-tip={sceneInEdit.name}>
            {sceneInEdit.name}&nbsp;/&nbsp;
          </Text>
          {(!sceneInEdit.roomIds || sceneInEdit.roomIds.length) === 0 && (
            <Text>No assigned rooms</Text>
          )}
          <Text>
            {sceneInEdit.roomIds &&
              sceneInEdit.roomIds.length < 3 &&
              sceneInEdit.roomIds
                .map(roomId => rooms.find(room => room.id === roomId).name)
                .join(', ')}
          </Text>
          {sceneInEdit.roomIds && sceneInEdit.roomIds.length > 2 && (
            <Text>More than 2 rooms</Text>
          )}
        </ConfPanelDetailsText>
        <EditIcon />
      </ConfPanelModalButton>
    );
  }
);

const SceneInEditDropdown = props => {
  const { rooms, docks, sceneInEdit, onSceneSave } = props;
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [
    sceneInEditDetailsInChange,
    setSceneInEditDetailsInChange
  ] = React.useState({
    name: '',
    rooms: null
  });
  const [selectedRoomsOption, setSelectedRoomsOption] = React.useState(null);
  const [roomsOptions, setRoomsOptions] = React.useState([]);
  const refreshRoomTypes = () => {
    const options = rooms
      .filter(
        room =>
          !sceneInEditDetailsInChange.rooms ||
          !sceneInEditDetailsInChange.rooms.some(
            ({ value }) => value === room.id
          )
      )
      .map(room => ({ value: room.id, label: room.name }));

    setRoomsOptions(options);
  };

  const resetSceneDetails = () => {
    if (rooms.length || docks.length) {
      setSceneInEditDetailsInChange({
        name: sceneInEdit.name,
        rooms: sceneInEdit.roomIds
          ? sceneInEdit.roomIds.map(roomId => ({
              value: roomId,
              label: rooms.find(room => room.id === roomId).name
            }))
          : []
      });
    }
  };

  React.useEffect(() => {
    resetSceneDetails();
  }, [sceneInEdit]);
  React.useEffect(() => {
    refreshRoomTypes();
  }, [rooms, sceneInEdit, sceneInEditDetailsInChange]);

  return (
    <Dropdown
      show={showDropdown}
      onToggle={() => {
        setShowDropdown(!showDropdown);
        resetSceneDetails();
      }}
    >
      <Dropdown.Toggle
        id="filters-button"
        as={CustomToggle}
        // @ts-ignore
        rooms={rooms}
        sceneInEdit={sceneInEdit}
      />
      <ConfPanelInEditModalButtons>
        <ConfPanelNameInput
          placeholder="Type scene name"
          label="Scene name"
          type="text"
          value={sceneInEditDetailsInChange.name}
          onChange={name =>
            setSceneInEditDetailsInChange({
              ...sceneInEditDetailsInChange,
              name
            })
          }
          testId="SceneNameInput"
        />
        <ConfPanelAddRoomButtonContainer>
          <LabeledSelect
            testId="SceneRoomsSelect"
            value={selectedRoomsOption}
            options={roomsOptions}
            placeholder="Select rooms"
            label="Select rooms"
            onChange={selectedRoom => setSelectedRoomsOption(selectedRoom)}
            className="configuration-panel-scene-add-room-select"
          />
          <ConfPanelAddRoomButton
            testId="AddSceneRoomBtn"
            colorType={ButtonColorType.WHITE}
            type="submit"
            disabled={!selectedRoomsOption}
            text="Add"
            onClick={() => {
              setSelectedRoomsOption(null);
              setSceneInEditDetailsInChange({
                ...sceneInEditDetailsInChange,
                rooms: [
                  ...sceneInEditDetailsInChange.rooms,
                  selectedRoomsOption
                ]
              });
            }}
          />
        </ConfPanelAddRoomButtonContainer>
        <Spacer />
        <ConfPanelFilterTags>
          {sceneInEditDetailsInChange.rooms &&
            sceneInEditDetailsInChange.rooms.map(selectedRoom => (
              <FilterTag
                key={`filter-tag-${selectedRoom.value}`}
                filterKey={selectedRoom.value}
                value={selectedRoom.label}
                onRemoveTag={removedRoomId =>
                  setSceneInEditDetailsInChange({
                    ...sceneInEditDetailsInChange,
                    rooms: [
                      ...sceneInEditDetailsInChange.rooms.filter(
                        room => room.value !== removedRoomId
                      )
                    ]
                  })
                }
              />
            ))}
        </ConfPanelFilterTags>
        <ConfPanelInEditSaveButton
          testId="SaveSceneDetailsBtn"
          colorType={ButtonColorType.WHITE}
          type="submit"
          disabled={
            sceneInEditDetailsInChange.name.length === 0 ||
            sceneInEditDetailsInChange.name.length > 60
          }
          text="Save"
          onClick={() => {
            onSceneSave(sceneInEditDetailsInChange);
            setShowDropdown(false);
          }}
        />
      </ConfPanelInEditModalButtons>
    </Dropdown>
  );
};

export default SceneInEditDropdown;
